import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'

interface ConfirmOptions {
  title?: string
  confirmLabel?: string
  cancelLabel?: string
  /** Style the confirm button as destructive (red). */
  danger?: boolean
}

interface NotifyOptions {
  title?: string
  okLabel?: string
}

interface DialogState {
  kind: 'confirm' | 'notify'
  message: string
  title?: string
  confirmLabel: string
  cancelLabel: string
  danger: boolean
  resolve: (value: boolean) => void
}

interface DialogApi {
  /** Ask the user to confirm an action; resolves true if they confirm. Replaces window.confirm. */
  confirm: (message: string, opts?: ConfirmOptions) => Promise<boolean>
  /** Show a message with a single OK button. Replaces window.alert. */
  notify: (message: string, opts?: NotifyOptions) => Promise<void>
}

const DialogContext = createContext<DialogApi | null>(null)

export function useDialogs(): DialogApi {
  const ctx = useContext(DialogContext)
  if (!ctx) throw new Error('useDialogs must be used within a DialogProvider')
  return ctx
}

/**
 * Renders a single in-app modal and exposes confirm()/notify() via context, so the
 * app uses styled dialogs instead of the browser's native alert()/confirm() popups.
 */
export function DialogProvider({ children }: { children: ReactNode }) {
  const [dialog, setDialog] = useState<DialogState | null>(null)
  const dialogRef = useRef<DialogState | null>(null)
  useEffect(() => { dialogRef.current = dialog }, [dialog])

  const confirm = useCallback((message: string, opts: ConfirmOptions = {}) =>
    new Promise<boolean>((resolve) => setDialog({
      kind: 'confirm',
      message,
      title: opts.title,
      confirmLabel: opts.confirmLabel ?? 'OK',
      cancelLabel: opts.cancelLabel ?? 'Avbryt',
      danger: opts.danger ?? false,
      resolve,
    })), [])

  const notify = useCallback((message: string, opts: NotifyOptions = {}) =>
    new Promise<void>((resolve) => setDialog({
      kind: 'notify',
      message,
      title: opts.title,
      confirmLabel: opts.okLabel ?? 'OK',
      cancelLabel: '',
      danger: false,
      resolve: () => resolve(),
    })), [])

  const close = useCallback((result: boolean) => {
    dialogRef.current?.resolve(result)
    dialogRef.current = null
    setDialog(null)
  }, [])

  // Esc cancels (resolves false / dismisses) while a dialog is open.
  useEffect(() => {
    if (!dialog) return
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') close(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [dialog, close])

  return (
    <DialogContext.Provider value={{ confirm, notify }}>
      {children}
      {dialog && (
        <div className="modal-overlay" onClick={() => close(false)}>
          <div className="modal card" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            {dialog.title && <h2>{dialog.title}</h2>}
            <p>{dialog.message}</p>
            <div className="modal-actions">
              {dialog.kind === 'confirm' && (
                <button onClick={() => close(false)}>{dialog.cancelLabel}</button>
              )}
              <button className={dialog.danger ? 'danger' : 'primary'} autoFocus onClick={() => close(true)}>
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  )
}
